import { ResolveIndex } from '@rbv/shared';
import { vault } from '../sync/vault.js';
import { index } from '../worker/client.js';

/**
 * Wikilink resolution for editor decorations. Two layers:
 *  1. A synchronous ResolveIndex built from the vault file map (names +
 *     paths; rebuilt lazily whenever the file map changes).
 *  2. The index worker's per-note resolved links (includes aliases); fetched
 *     asynchronously and preferred when available.
 * Subscribers are notified when either layer changes so decorations rebuild.
 */

let syncIndex: ResolveIndex | null = null;
let wired = false;
const listeners = new Set<() => void>();

function notify() {
  for (const cb of listeners) cb();
}

function wire() {
  if (wired) return;
  wired = true;
  vault.onFiles(() => {
    syncIndex = null;
    notify();
  });
  index.onChange(() => notify());
}

function fileIndex(): ResolveIndex {
  if (!syncIndex) syncIndex = new ResolveIndex(vault.files.entries());
  return syncIndex;
}

export interface Resolved {
  id: string | null;
  path: string | null;
}

export class LinkResolver {
  /** targetName (lowercased) -> resolved id | null, from the index worker. */
  private workerMap = new Map<string, string | null>();
  private disposed = false;
  private unsubs: Array<() => void> = [];
  private scheduled = false;

  constructor(
    readonly noteId: string,
    private onChange: () => void,
  ) {
    wire();
    const schedule = () => {
      if (this.scheduled || this.disposed) return;
      this.scheduled = true;
      queueMicrotask(() => {
        this.scheduled = false;
        if (!this.disposed) this.onChange();
      });
    };
    listeners.add(schedule);
    this.unsubs.push(() => listeners.delete(schedule));
    // Refresh worker-resolved links whenever the index changes.
    const refetch = () => void this.fetchWorkerLinks();
    this.unsubs.push(index.onChange(refetch));
    void this.fetchWorkerLinks();
    // Embedded notes re-render when their content changes elsewhere.
    this.unsubs.push(
      vault.onNoteContent((id) => {
        if (id !== this.noteId) schedule();
      }),
    );
  }

  private async fetchWorkerLinks() {
    try {
      const entry = await index.note(this.noteId);
      if (this.disposed || !entry) return;
      const next = new Map<string, string | null>();
      for (const l of entry.links) next.set(l.targetName.toLowerCase(), l.targetId);
      let changed = next.size !== this.workerMap.size;
      if (!changed) {
        for (const [k, v] of next) {
          if (this.workerMap.get(k) !== v) {
            changed = true;
            break;
          }
        }
      }
      this.workerMap = next;
      if (changed) this.onChange();
    } catch {
      /* worker not ready — sync index covers us */
    }
  }

  private fromPath(): string {
    return vault.getEntry(this.noteId)?.path ?? '';
  }

  /** Resolve a wikilink target to a file id + path (null id = unresolved). */
  resolve(target: string): Resolved {
    if (target === '') {
      // [[#heading]] — same note.
      return { id: this.noteId, path: this.fromPath() || null };
    }
    let id: string | null | undefined = this.workerMap.get(target.toLowerCase());
    if (id === undefined) id = fileIndex().resolve(target, this.fromPath());
    if (!id) return { id: null, path: null };
    const entry = vault.getEntry(id);
    if (!entry || entry.deleted) return { id: null, path: null };
    return { id, path: entry.path };
  }

  dispose() {
    this.disposed = true;
    for (const u of this.unsubs) u();
    this.unsubs = [];
  }
}

/** One-shot resolution for reading mode (no subscription). */
export function resolveOnce(target: string, fromNoteId: string): Resolved {
  wire();
  if (target === '') {
    const p = vault.getEntry(fromNoteId)?.path ?? null;
    return { id: fromNoteId, path: p };
  }
  const fromPath = vault.getEntry(fromNoteId)?.path ?? '';
  const id = fileIndex().resolve(target, fromPath);
  if (!id) return { id: null, path: null };
  const entry = vault.getEntry(id);
  if (!entry || entry.deleted) return { id: null, path: null };
  return { id, path: entry.path };
}
